import { useAxios, useHealthblocksSWR } from '@healthblocks-io/core'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { usePatientDocumentMarkAsRead } from '@/modules/documents/hooks/usePatientDocumentMarkAsRead'
import { useBadgeCount } from '@/modules/notifications/hooks/useBadgeCount'
import { DeeplinkNotFound } from '@/modules/shared/components/deeplink-not-found/DeeplinkNotFound'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { InfoMessageDetail } from '../components/message-detail/info-message/InfoMessageDetail'
import { PatientCommunicationDetail } from '../components/message-detail/patient-communication/PatientCommunicationDetail'
import { useUnreadMessageCount } from '../hooks/useUnreadMessageCount'

import type { Message } from '../types/message.types'

export const MessageDetailPage = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const { id } = useParams()
  const hbAxios = useAxios()
  const { updateBadgeCount } = useBadgeCount()

  const { checkUnreadMessageCount } = useUnreadMessageCount()
  const { markAsRead } = usePatientDocumentMarkAsRead()

  const {
    data: message,
    mutate: fetchMessage,
    error,
    isLoading,
  } = useHealthblocksSWR<Message>(`/message/${id}`, {
    shouldRetryOnError: false,
    onSuccess: async (message) => {
      if (message.readStatus === 'unread') {
        try {
          await hbAxios.patch(`/message/${message.id}/mark-as-read`)
          await checkUnreadMessageCount()
          updateBadgeCount()
        } catch (error) {
          // Handle any errors from the patch request if necessary
          console.error('Failed to mark message as read:', error)
        }
      }
    },
  })

  useHideNativeNavigation()

  useEffect(() => {
    if (id) {
      fetchMessage()
    }
  }, [id])

  // Render
  // ------------------------------------------------------------------------- /

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return <DeeplinkNotFound origin='message' />
  }

  if (!message) {
    return <DeeplinkNotFound origin='message' />
  }

  switch (message.type) {
    case 'Message': {
      return <InfoMessageDetail message={message} />
    }

    case 'PatientCommunication': {
      return (
        <PatientCommunicationDetail
          message={message}
          refetch={fetchMessage}
          onAttachmentOpen={() => {
            if (id) {
              markAsRead(id, undefined, { scope: 'attachment' })
            }
          }}
        />
      )
    }

    default: {
      return <DeeplinkNotFound origin='message' />
    }
  }
}
